import { Check, Clear, Shower } from '@mui/icons-material';
import { Avatar, Card, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';
import { TempBarIcon, TempCellarIcon, TempRemoteIcon } from '../../assets/custom_icons/customIcons';
import type { IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';

interface AvaniTempCardDisplayProps extends IIsPdf {
  id: number;
  temp: number | null;
  title: string;
  type: 'Cellar' | 'Remote' | 'Bar';
  desc: string;
  isOutOfRange: boolean;
  sx?: any;
  onClick?: () => void;
}

const AvaniStyledTempCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isPDF'
})<{ isPDF: boolean }>(({ theme, isPDF }) => {
  const currentTheme = isPDF ? AvaniLightTheme : theme;

  return `
    border-radius: 16px;
    padding: 8px 0;
  
    &.AvaniCardSmall {
      padding: 16px 0 8px 0;
    }
  
    .MuiTypography-root.MuiTypography-h4 {
      margin: 0 16px;
      line-height: 1;
      font-size: 20px;
      // padding-bottom: 24px;
    }
  
    .MuiTypography-root.MuiTypography-h4.AvaniCardTitleTemp {
      color: ${currentTheme.colors.avani.magenta}
    }
`;
});

export const AvaniTempCardDisplay: FC<AvaniTempCardDisplayProps> = ({
  temp,
  id,
  title,
  type,
  desc,
  isOutOfRange,
  sx,
  isPDF = false
}) => {
  const theme = useTheme();

  const tempValue =
    temp !== null ? `${temp}°C` : isOutOfRange ? <Clear sx={{ fontSize: 22 }} /> : <Check sx={{ fontSize: 22 }} />;
  const headingIconDimmensions = { width: 36, height: 36 };

  let headingIcon;
  switch (type) {
    case 'Bar':
      headingIcon = TempBarIcon({ ...headingIconDimmensions, isPDF });
      break;
    case 'Cellar':
      headingIcon = TempCellarIcon({ ...headingIconDimmensions, isPDF });
      break;
    case 'Remote':
      headingIcon = TempRemoteIcon({ ...headingIconDimmensions, isPDF });
      break;
    default:
      headingIcon = <Shower fontSize="large" sx={{ fontSize: '32px' }} />;
      break;
  }

  return (
    <span>
      <AvaniStyledTempCard
        isPDF={isPDF}
        raised={true}
        id={id.toString()}
        sx={{
          height: '120px',
          backgroundColor: isOutOfRange ? theme.colors.avani.lightMagenta : theme.colors.avani.lightTurc,
          ...sx
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h4"
              color={theme.colors.avani.teal}
              className={isOutOfRange ? 'AvaniCardTitleTemp' : ''}
              sx={{ flexGrow: 1 }}
            >
              {type === 'Bar' ? 'Dispense' : type}
            </Typography>
            <span>{headingIcon}</span>
          </div>
          <Typography
            variant="h5"
            color={isOutOfRange ? theme.colors.avani.magenta : theme.colors.avani.teal}
            sx={{ flexGrow: 1, marginX: '16px', lineHeight: 1, fontSize: '16px' }}
          >
            {title}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontSize={16} sx={{ px: 2, flexGrow: 1 }}>
              {desc}
            </Typography>
            <Avatar
              sx={{
                display: 'inline-flex',
                bgcolor: isOutOfRange ? theme.colors.avani.magenta : theme.colors.avani.teal,
                width: 42,
                height: 42,
                borderRadius: '50%',
                paddingTop: temp ? '3px' : undefined,
                float: 'right',
                marginRight: 2
              }}
            >
              {tempValue}
            </Avatar>
          </div>
        </div>
      </AvaniStyledTempCard>
    </span>
  );
};
